import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { initGA, trackPageView, analyticsPath } from './utils/analytics';
import { workspaceMainClass } from './utils/siteShell';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import FeedPage from './pages/FeedPage';
import ArticlePage from './pages/ArticlePage';
import NewsPage from './pages/NewsPage';
import StatsPage from './pages/StatsPage';
import AdminAgentsPage from './pages/AdminAgentsPage';
import AdminFeedsPage from './pages/AdminFeedsPage';
import AdminCopyrightPage from './pages/AdminCopyrightPage';
import AdminPipelineOpsPage from './pages/AdminPipelineOpsPage';
import AgentsHubPage from './pages/AgentsHubPage';
import AgentCategoryPage from './pages/AgentCategoryPage';
import ResearchPage from './pages/ResearchPage';
import AuthorChannelPage from './pages/AuthorChannelPage';
import AuthorArticlePage from './pages/AuthorArticlePage';
import AuthorsPage from './pages/AuthorsPage';
import StringersPage from './pages/StringersPage';
import AuthorProfilePage from './pages/AuthorProfilePage';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import DeskPage from './pages/DeskPage';
import WebPage from './pages/WebPage';
import EntityPage from './pages/EntityPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import CopyrightPage from './pages/CopyrightPage';
import OAuthCallback from './components/OAuthCallback';
import './styles/App.css';
import './styles/MorePage.css';

// Component to track page views
const AnalyticsWrapper = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(analyticsPath(location.pathname, location.search));
  }, [location]);
  
  return children;
};

const AppShell = () => {
  const { pathname } = useLocation();
  const workspaceMod = workspaceMainClass(pathname);
  const mainClassName = workspaceMod ? `main-content ${workspaceMod}` : 'main-content';

  return (
    <div className="App">
      <ScrollToTop />
      <Header />
      <main className={mainClassName}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/article/:id" element={<ArticlePage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:section" element={<NewsPage />} />
          <Route path="/desk" element={<DeskPage />} />
          <Route path="/web" element={<WebPage />} />
          <Route path="/entity/:slug" element={<EntityPage />} />
          <Route path="/stringers" element={<StringersPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/settings/profile" element={<AuthorProfilePage />} />
          <Route path="/authors" element={<AuthorsPage />} />
          <Route path="/authors/:slug" element={<AuthorChannelPage />} />
          <Route path="/authors/:slug/:articleId" element={<AuthorArticlePage />} />
          <Route path="/agents" element={<AgentsHubPage />} />
          <Route path="/agents/:slug" element={<AgentCategoryPage />} />
          <Route path="/admin/agents" element={<AdminAgentsPage />} />
          <Route path="/admin/feeds" element={<AdminFeedsPage />} />
          <Route path="/admin/copyright" element={<AdminCopyrightPage />} />
          <Route path="/admin/pipeline-ops" element={<AdminPipelineOpsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/oauth2/code/google" element={<OAuthCallback />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/copyright" element={<CopyrightPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <AnalyticsWrapper>
            <AppShell />
          </AnalyticsWrapper>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App; 